import React, { useState } from "react";
import PropTypes from "prop-types";

import { Dialog, PasswordField } from "../../components/DCKeeper";
import { passwordApi } from "../../helpers/dck_helper";
import { useSessionStore } from "../../stores/sessionStore";

/**
 * 登入後自己改密碼（會員選單 →「重設密碼」，Figma 24:9203 / 成功彈窗 28:10599）。
 *
 * 與 /auth-recoverpw 的「忘記密碼」是兩條路:那條要寄一次性連結,這條人已經登入,
 * 所以驗的是舊密碼。改成功後換成設計稿 28:10599 的「密碼已重設」提示,
 * 提醒非本人操作要聯繫管理者 —— 這是帳號被盜時唯一的線索,不能省。
 *
 * force=true 用於首次登入強制變更(ticket 34):對話框關不掉,改完才放行。
 * 管理者設的是「初始密碼」,不該一直當成這個人的密碼用下去。
 */
const HINT = "至少 8 碼，含大小寫英文與數字";
const DONE_TEXT =
  "您的密碼已重設成功，請以新密碼登入；若非您本人操作，請立即聯繫帳號管理者。";

const ChangePasswordDialog = ({ isOpen, toggle, force = false }) => {
  const updateUser = useSessionStore((s) => s.updateUser);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // 關閉時清乾淨,下次開不會殘留上一輪打過的字或錯誤訊息
  // 改完之後才解除首登阻擋 —— 而且是等使用者把「密碼已重設」讀完按下確認,
  // 不是送出成功的當下:那會讓外層立刻卸載彈窗,提示連閃都不閃就不見了。
  const close = () => {
    if (done) updateUser({ mustChangePassword: false });
    setOldPassword("");
    setNewPassword("");
    setError("");
    setDone(false);
    if (toggle) toggle();
  };

  const submit = async () => {
    if (!oldPassword) { setError("請輸入舊密碼。"); return; }
    setError("");
    setBusy(true);
    try {
      // 後端契約(ticket 09):當前使用者由 JWT 識別,不帶 userId。
      // 設計稿 24:9203 只有舊/新兩欄,confirm_new_password 以同值補滿足契約必填
      await passwordApi.changePassword({
        old_password: oldPassword,
        new_password: newPassword,
        confirm_new_password: newPassword,
      });
      setDone(true);
    } catch (err) {
      // api_helper 的攔截器 reject 的是「已轉譯的字串」,不是 Error
      setError(typeof err === "string" ? err : "密碼變更失敗，請稍後再試。");
    } finally {
      setBusy(false);
    }
  };

  // 強制模式:不給 toggle → 右上角 ×、取消鈕、點背景與 Esc 全部關不掉
  const dismiss = force ? undefined : close;

  if (done) {
    return (
      <Dialog isOpen={isOpen} toggle={dismiss} title="密碼已重設" onConfirm={close}>
        {DONE_TEXT}
      </Dialog>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      toggle={dismiss}
      title="重設密碼"
      onConfirm={submit}
      confirmLoading={busy}
    >
      <PasswordField
        label="舊密碼"
        name="old-password"
        required
        value={oldPassword}
        onChange={(v) => { setOldPassword(v); setError(""); }}
      />
      <PasswordField
        label="新密碼"
        name="new-password"
        required
        help={HINT}
        error={error}
        value={newPassword}
        onChange={(v) => { setNewPassword(v); setError(""); }}
      />
    </Dialog>
  );
};
ChangePasswordDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func,
  // 首次登入強制變更:關不掉
  force: PropTypes.bool,
};

export default ChangePasswordDialog;
